// Service worker registration helper. Prod-only: vite-plugin-pwa injects the
// registration call automatically when injectRegister is set; we still expose
// this helper so main.ts can await precache installation as part of the READY
// gate (amendment 1) and so dev mode aggressively unregisters any prior worker.

import { registerSW } from 'virtual:pwa-register';

export interface SwReadiness {
  supported: boolean;
  ready: Promise<void>;
}

export function installServiceWorker(): SwReadiness {
  if (import.meta.env.DEV) {
    // Dev: unregister any stale SW so Playwright never hits a cached build.
    void navigator.serviceWorker?.getRegistrations().then((regs) => {
      for (const r of regs) void r.unregister();
    });
    return { supported: false, ready: Promise.resolve() };
  }
  let resolveReady: () => void = () => {};
  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });
  const update = registerSW({
    immediate: true,
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      // Wait until the active worker is installed.
      const check = () => {
        if (registration.active) {
          // Confirm the precache is in place by bouncing a message.
          const channel = new MessageChannel();
          channel.port1.onmessage = () => resolveReady();
          navigator.serviceWorker.controller?.postMessage('sw-precache-ready', [channel.port2]);
          // Fallback: don't block forever on a missing ack.
          window.setTimeout(resolveReady, 1500);
        } else {
          window.setTimeout(check, 250);
        }
      };
      void check();
    },
  });
  void update;
  return { supported: true, ready };
}
